import { useState } from 'react';
import { DEFAULT_IMAGES } from '../../constants/defaults';

/**
 * Componente Avatar - Muestra foto de perfil del usuario
 * Optimizado para carga rápida sin flashes
 * Sin Premium: foto/banner se eliminan al expirar (se muestra placeholder/iniciales)
 */
const Avatar = ({
  src,
  alt = 'Avatar',
  size = 'md',
  fallbackText,
  className = '',
  showOnlineIndicator = false,
  isOnline = false,
  editable = false,
  onEdit,
  showRing = false,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);

  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl',
    '3xl': 'w-24 h-24 text-3xl'
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const imageSrc = imageError || !src ? DEFAULT_IMAGES.PROFILE : src;

  const getInitials = (text) => {
    if (!text) return '?';
    const words = text.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return text.substring(0, 2).toUpperCase();
  };

  return (
    <div className={`relative inline-block ${className}`} {...props}>
      <div
        className={`
          ${sizes[size]}
          rounded-full
          overflow-hidden
          ${(!src || imageError) ? 'bg-gradient-to-br from-indigo-600 to-purple-600' : 'bg-transparent'}
          flex items-center justify-center
          shadow-md
          ${showRing ? 'ring-2 ring-white dark:ring-slate-900' : ''}
          ${editable ? 'cursor-pointer group' : ''}
        `}
        onClick={editable ? onEdit : undefined}
      >
        {src && !imageError ? (
          <img
            src={imageSrc}
            alt={alt}
            className="w-full h-full object-cover object-center"
            style={{
              backgroundColor: 'transparent',
              imageRendering: 'crisp-edges'
            }}
            onError={handleImageError}
            loading="eager"
            decoding="async"
          />
        ) : fallbackText ? (
          <span className="text-white font-semibold select-none">
            {getInitials(fallbackText)}
          </span>
        ) : (
          <svg className="w-3/4 h-3/4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        )}

        {editable && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200">
            <svg className="w-1/2 h-1/2 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        )}
      </div>

      {showOnlineIndicator && (
        <div
          className={`
            absolute bottom-0 right-0
            w-3 h-3
            rounded-full
            border-2 border-white dark:border-slate-900
            ${isOnline ? 'bg-green-500' : 'bg-gray-400'}
          `}
        />
      )}
    </div>
  );
};

export default Avatar;
